import { JSX } from 'react'

import { useTheme } from '@chakra-ui/react'
import { AlertCircleIcon, LoadingDotsIcon } from '@verbit-ai/icons-library'

import { TranscriptError } from 'src/models/Error'

import * as S from './styles'

type TranscriptErrorScreenProps = {
    error: TranscriptError
}

export const TranscriptErrorScreen = (props: TranscriptErrorScreenProps) => {
    const { error } = props
    const {
        palette: {
            global: { iconColor },
        },
    } = useTheme()

    const ERROR_DETAILS: Record<TranscriptError, JSX.Element> = {
        errorAccessDenied: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Title>Access Denied</S.Title>
                <S.Description>
                    You don&apos;t have permission to access this transcript. The link might be private or require authentication
                </S.Description>
            </>
        ),
        errorInvalidLink: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Title>Invalid Link</S.Title>
                <S.Description>The link you provided is invalid or broken. Please double-check the link and try again</S.Description>
            </>
        ),
        errorNetworkError: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Title>Network Error</S.Title>
                <S.Description>
                    There seems to be a problem connecting to the server. Please check your internet connection and try again
                </S.Description>
            </>
        ),
        errorServerOverload: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Title>Server Overload</S.Title>
                <S.Description>The server is currently not responding. Please try accessing the transcript again later</S.Description>
            </>
        ),
        errorTechnicalDifficulties: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Title>Technical Difficulties</S.Title>
                <S.Description>
                    There are currently technical difficulties with the live transcript service. Please try again later
                </S.Description>
            </>
        ),
        errorTranscriptNotFound: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Title>Transcript Not Found</S.Title>
                <S.Description>
                    The transcript you are trying to access cannot be found. It might have been deleted or unavailable
                </S.Description>
            </>
        ),
        errorConnectionError: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Description>
                    A connection error occurred.
                    <br />
                    Please contact support
                </S.Description>
            </>
        ),
        errorInvalidToken: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Description>
                    It looks like the link you provided is invalid or broken. If you believe this is an error, please contact support or
                    double-check the link and try again
                </S.Description>
            </>
        ),
        errorNoFutureSessions: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Description>
                    It looks like there isn&apos;t a session scheduled at this link right now. Please check back later, or confirm the
                    session details with your organizer
                </S.Description>
            </>
        ),
        errorRedirectingToCorrectRegion: (
            <>
                <LoadingDotsIcon size={28} />
                <S.Description>Redirecting to the right region...</S.Description>
            </>
        ),
        errorDocumentProcessingError: (
            <>
                <AlertCircleIcon size={75} color={iconColor} />
                <S.Title>Document Processing Error</S.Title>
                <S.Description>
                    There was an error processing the uploaded document. Please try again later or contact support.
                </S.Description>
            </>
        ),
    }

    return (
        <S.Wrapper>
            <S.Text className={error}>{ERROR_DETAILS[error]}</S.Text>
        </S.Wrapper>
    )
}
